import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaMoon, FaSun } from 'react-icons/fa';
import { HashLink } from 'react-router-hash-link';
import Button from '../ui/Button';
import { publicApi } from '../../services/publicApi';

const Hero = ({ theme, onToggleTheme }) => {
  const [heroContent, setHeroContent] = useState({
    badgeText: 'LEADING TECH SOLUTIONS IN SOMALIA',
    title: "Let's Make Your",
    highlightText: 'Business Flourish',
    description:
      'Streamline operations with our tailored ERP systems, innovative software development, and comprehensive cloud services. We believe that the prospect presented by technology has never been more spacious.',
    primaryButtonText: 'Get Started',
    primaryButtonUrl: '#contact',
    imageUrl: '/img/hr8.png',
  });

  useEffect(() => {
    let isMounted = true;
    const loadHero = async () => {
      try {
        const data = await publicApi.getHeroSections();
        const selected = data?.find((item) => item.isActive) || data?.[0];
        if (!selected || !isMounted) return;

        setHeroContent((prev) => ({
          ...prev,
          badgeText: selected.badgeText || prev.badgeText,
          title: selected.title || prev.title,
          highlightText: selected.highlightText || prev.highlightText,
          description: selected.description || prev.description,
          primaryButtonText: selected.primaryButtonText || prev.primaryButtonText,
          primaryButtonUrl: selected.primaryButtonUrl || prev.primaryButtonUrl,
          imageUrl: selected.imageUrl || prev.imageUrl,
        }));
      } catch {
        // Keep static fallback content if API is unavailable.
      }
    };

    loadHero();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section id="home" className="relative min-h-[74vh] md:min-h-[80vh] flex flex-col items-center justify-center pt-32 pb-24 md:pb-28 overflow-hidden bg-white dark:bg-black transition-colors duration-300">

      {/* Abstract Background Gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-1/2 transform -translate-x-1/2 w-[120%] h-[80%] rounded-[100%] bg-gradient-to-b from-primary/15 dark:from-primary/20 to-transparent blur-[100px]"></div>
        <div className="hidden dark:block absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(243,199,21,0.14),transparent_45%)]"></div>
      </div>

      {/* Floating Theme Toggle */}
      <div className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 z-20">
        <button
          onClick={onToggleTheme}
          aria-label="Toggle theme"
          className="w-10 h-10 bg-white dark:bg-[#101010] border border-gray-200 dark:border-white/10 rounded-xl flex items-center justify-center text-black dark:text-white shadow-sm hover:shadow-md transition-shadow"
        >
          {theme === 'dark' ? <FaSun size={15} /> : <FaMoon size={15} />}
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full mt-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">

          {/* Left Side - Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center lg:items-start text-center lg:text-left flex-1"
          >
            {/* Badge */}
            <div className="mb-8 inline-block px-5 py-1.5 rounded-full border border-primary/30 bg-white dark:bg-[#0f0f0f] text-primary text-xs font-bold tracking-widest uppercase">
              {heroContent.badgeText}
            </div>

            {/* Main Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-black dark:text-white leading-[1.1] mb-6 tracking-tight">
              {heroContent.title} <br />
              {heroContent.highlightText ? (
                <span className="text-primary inline-block mt-2">{heroContent.highlightText}</span>
              ) : null}
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-300 mb-12 max-w-2xl leading-relaxed">
              {heroContent.description}
            </p>

            {/* Action / Search Input */}
            <div className="w-full max-w-md relative group">
              <input
                type="text"
                placeholder="Enter your business email"
                className="w-full pl-6 pr-32 py-4 rounded-full border border-gray-200 dark:border-white/10 shadow-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all bg-white dark:bg-[#111111] text-black dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
              {heroContent.primaryButtonUrl ? (
                <HashLink
                  smooth
                  to={heroContent.primaryButtonUrl.startsWith('#') ? `/${heroContent.primaryButtonUrl}` : heroContent.primaryButtonUrl}
                  className="absolute right-1.5 top-1.5 bottom-1.5"
                >
                  <Button variant="primary" className="h-full px-6 rounded-full text-sm font-bold shadow-none hover:shadow-md">
                    {heroContent.primaryButtonText}
                  </Button>
                </HashLink>
              ) : (
                <Button variant="primary" className="absolute right-1.5 top-1.5 bottom-1.5 px-6 rounded-full text-sm font-bold shadow-none hover:shadow-md">
                  {heroContent.primaryButtonText}
                </Button>
              )}
            </div>
          </motion.div>

          {/* Right Side - Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex-1 flex items-center justify-center"
          >
            <img
              src={heroContent.imageUrl}
              alt="Raadso Tech - Business Technology Solutions"
              className="w-full max-w-lg lg:max-w-xl xl:max-w-2xl h-auto object-contain drop-shadow-2xl"
            />
          </motion.div>

        </div>
      </div>


    </section>
  );
};

export default Hero;
